/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-undef */
import 'amazon-connect-streams';
import {
  API_PATIENTS_COMMENTS,
  API_READINGS_ACTIONS,
  API_CCM_PATIENT_CALL,
} from 'services/dataService/resources';
import { showResult } from 'infrastructure/hooks/utils';
import { SocketRoutes } from 'services/wsService';
import { differenceInSeconds } from 'date-fns';
import { emitCallEndedEvent } from 'infrastructure/customEvents/callEvents';

import dataService from '../dataService';

import type WS from 'services/wsService';
import type { Orchestration } from 'aws-rum-web/dist/cjs/orchestration/Orchestration';

const AWS_CONNECT_URL_CCP = process.env.REACT_APP_CONNECT_URL_CCP!;
const AWS_CONNECT_URL = process.env.REACT_APP_CONNECT_URL!;

interface ConnectDetails {
  patientGuid: string;
  connectedAt?: Date;
}

const contactToPatientMap: Map<string, ConnectDetails> = new Map();

const init = (
  phoneNumber: string,
  patientGuid: string,
  setCallId: (callId: string) => void,
  isRpmCall: boolean,
  deviceId: string,
  readingId: string,
  wsClient: typeof WS,
  awsRumAgent: Orchestration | null,
  isReadOnlyRole: boolean,
) => {
  const containerDiv = document.getElementById('connect-el')!;

  connect.core.initCCP(containerDiv, {
    ccpUrl: AWS_CONNECT_URL_CCP,
    loginUrl: AWS_CONNECT_URL,
    loginPopup: true,
    loginPopupAutoClose: true,
    region: 'us-east-1',
    pageOptions: {
      enableAudioDeviceSettings: false,
      enablePhoneTypeSettings: false,
    },
    softphone: {
      allowFramedSoftphone: true,
    },
  });

  if (phoneNumber) {
    connect.agent((agent) => {
      const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber);
      agent.connect(endpoint);
    });

    connect.contact((contact) => {
      contact.onConnecting(async (event) => {
        if (event.isInbound()) return;
        if (!patientGuid) return;

        const { contactId } = event;
        setCallId(contactId);
        awsRumAgent?.recordEvent('callConnecting', {
          patientGuid,
          phoneNumber,
          contactId,
        });
        let callData = {};
        callData = { patientGuid };

        contactToPatientMap.set(contactId, { patientGuid });
        wsClient.send(SocketRoutes.OnCallStarted, { patientGuid, isRpmCall });

        if (isRpmCall) {
          callData = {
            ...callData,
            deviceId,
            readingId,
            type: 'call',
            message: contactId,
            commentType: 'rpm',
          };
        } else {
          callData = {
            ...callData,
            callId: contactId,
          };
        }

        if (!isReadOnlyRole) {
          const { error } = await dataService.createOne(
            !isRpmCall
              ? API_CCM_PATIENT_CALL
              : deviceId && readingId
              ? API_READINGS_ACTIONS
              : API_PATIENTS_COMMENTS,
            callData,
          );

          if (error) showResult(error);
        }

        patientGuid = '';
      });

      contact.onConnected(async (event) => {
        if (event.isInbound()) return;
        const { contactId } = event;
        const currentPatient = contactToPatientMap.get(contactId);
        if (currentPatient) {
          currentPatient.connectedAt = new Date();
        }
      });

      contact.onDestroy(async (event) => {
        if (event.isInbound()) return;
        const { contactId } = event;
        const currentPatientGuid =
          contactToPatientMap.get(contactId)?.patientGuid;
        if (currentPatientGuid) {
          awsRumAgent?.recordEvent('callDestroyed', {
            patientGuid: currentPatientGuid,
            contactId,
          });
          wsClient.send(SocketRoutes.OnCallEnded, {
            patientGuid: currentPatientGuid,
            isRpmCall,
          });
        }
      });

      contact.onEnded(async (event) => {
        if (event.isInbound()) return;
        const { contactId } = event;
        const currentPatient = contactToPatientMap.get(contactId);
        if (currentPatient) {
          const currentPatientGuid = currentPatient.patientGuid;

          awsRumAgent?.recordEvent('callDestroyed', {
            patientGuid: currentPatientGuid,
            contactId,
          });
          wsClient.send(SocketRoutes.OnCallEnded, {
            patientGuid: currentPatientGuid,
            isRpmCall,
          });

          const callDurationSeconds = currentPatient.connectedAt
            ? differenceInSeconds(new Date(), currentPatient.connectedAt)
            : 0;
          emitCallEndedEvent({
            patientGuid: currentPatientGuid,
            callDuration: callDurationSeconds,
          });
        }
      });
    });
  }
};

const terminate = () => {
  connect.core.terminate();
  const containerDiv = document.getElementById('connect-el');
  if (containerDiv) {
    const iframe = containerDiv.firstElementChild!;
    containerDiv.removeChild(iframe);
  }
};

export { init, terminate };
